import React, { useState, useEffect } from 'react';
import { Search, X, ChevronLeft, ChevronRight, Download, Filter } from 'lucide-react';

const Table = ({ headers, data, actions, emptyMessage = "No hay datos disponibles", itemsPerPage = 10, showSearch = true }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // 🛡️ Sincronización Antigravity: Resetear página al cambiar los datos
  useEffect(() => {
    setCurrentPage(1);
  }, [data, searchTerm]);

  // 🔍 Búsqueda Profunda Antigravity: Busca tanto en las celdas como en el objeto original (_raw)
  const filteredData = data.filter(row => {
    const searchString = searchTerm.toLowerCase();
    
    // 1. Buscar en el objeto de datos original (si existe)
    const matchesRaw = row._raw ? Object.values(row._raw).some(val => 
      String(val).toLowerCase().includes(searchString)
    ) : false;

    // 2. Buscar en las propiedades de la fila (para texto plano)
    const matchesRow = Object.keys(row).filter(k => !k.startsWith('_')).some(k => 
      String(row[k]).toLowerCase().includes(searchString)
    );

    return matchesRaw || matchesRow;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, startIndex + itemsPerPage);

  const clearSearch = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };

  return (
    <div className="flex flex-col space-y-4">
      {/* Table Header / Toolbar */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 px-6 pt-6 pb-2">
        {showSearch && (
          <div className="relative w-full md:w-96 group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30 group-focus-within:text-primary-light transition-colors" size={18} />
            <input 
              type="text" 
              placeholder="Buscar en la tabla..." 
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              className="w-full bg-white/5 border border-white/10 rounded-2xl py-3 pl-12 pr-12 text-sm outline-none focus:border-primary-light/40 focus:bg-white/10 transition-all font-medium"
            />
            {searchTerm && (
              <button 
                onClick={clearSearch}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 p-1 rounded-lg text-white/50 hover:text-white transition-all flex items-center gap-1 text-[10px] uppercase font-bold"
              >
                Clear <X size={10} />
              </button>
            )}
          </div>
        )}

      </div>

      {/* Table Container */}
      <div className="overflow-x-auto custom-scrollbar px-6">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-white/10">
              {headers.map((h, i) => (
                <th key={i} className="text-left py-5 px-4 text-[10px] uppercase tracking-[2px] font-extrabold text-white/40">
                  {h}
                </th>
              ))}
              {actions && <th className="text-right py-5 px-4 text-[10px] uppercase tracking-[2px] font-extrabold text-white/40">Acciones</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {paginatedData.length > 0 ? paginatedData.map((row, i) => (
              <tr 
                key={i} 
                className="group hover:bg-white/[0.02] transition-colors"
              >
                {Object.keys(row).filter(k => !k.startsWith('_')).map((key, j) => (
                  <td key={j} className="py-5 px-4 text-sm font-medium text-white/80 group-hover:text-white transition-colors">
                    {row[key]}
                  </td>
                ))}
                {actions && (
                  <td className="py-5 px-4 text-right">
                    <div className="flex justify-end gap-2">
                       {actions(row)}
                    </div>
                  </td>
                )}
              </tr>
            )) : (
              <tr>
                <td colSpan={headers.length + (actions ? 1 : 0)} className="py-20 text-center text-white/30 italic text-sm">
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Table Footer / Pagination */}
      <div className="flex flex-col sm:flex-row justify-between items-center px-6 py-6 border-t border-white/5 gap-4">
        <div className="text-xs text-white/30 font-medium">
          Mostrando <span className="text-white font-bold">{Math.min(startIndex + 1, filteredData.length)}</span> a <span className="text-white font-bold">{Math.min(startIndex + itemsPerPage, filteredData.length)}</span> de <span className="text-white font-bold">{filteredData.length}</span> registros
        </div>
        <div className="flex items-center gap-2">
          <button 
             disabled={currentPage === 1}
             onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
             className="glass p-2 rounded-xl disabled:opacity-20 disabled:cursor-not-allowed hover:bg-white/10 transition-all"
          >
            <ChevronLeft size={20} />
          </button>
          
          <div className="flex gap-1 px-2">
             {[...Array(totalPages)].map((_, i) => (
               <button 
                 key={i}
                 onClick={() => setCurrentPage(i + 1)}
                 className={`w-10 h-10 rounded-xl text-xs font-bold transition-all ${currentPage === i + 1 ? 'bg-primary shadow-lg shadow-primary/30 text-white' : 'glass hover:bg-white/10 text-white/50'}`}
               >
                 {i + 1}
               </button>
             ))}
          </div>

          <button 
             disabled={currentPage === totalPages || totalPages === 0}
             onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
             className="glass p-2 rounded-xl disabled:opacity-20 disabled:cursor-not-allowed hover:bg-white/10 transition-all"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>
      
      <style dangerouslySetInnerHTML={{ __html: `
        .custom-scrollbar::-webkit-scrollbar { height: 6px; }
        .custom-scrollbar-track { background: transparent; }
        .custom-scrollbar-thumb { background: rgba(255,255,255,0.05); border-radius: 10px; }
      `}} />
    </div>
  );
};

export default Table;
